import React, { useState, useRef, useEffect, useCallback } from 'react';

interface SliderCaptchaProps {
  visible: boolean;
  onSuccess: () => void;
  onClose: () => void;
}

// 拼图块参数
const CANVAS_WIDTH = 320;
const CANVAS_HEIGHT = 180;
const PIECE_SIZE = 44;
const PIECE_RADIUS = 8;
const TOLERANCE = 5; // 允许误差（px）

// 随机整数
const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

// 绘制拼图块路径（带凸起/凹陷）
const drawPiecePath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  radius: number
) => {
  const half = size / 2;
  ctx.beginPath();
  // 上边
  ctx.moveTo(x, y);
  ctx.lineTo(x + half - radius, y);
  ctx.arc(x + half, y - radius * 0.4, radius, Math.PI * 0.7, Math.PI * 0.3, false);
  ctx.lineTo(x + size, y);
  // 右边
  ctx.lineTo(x + size, y + half - radius);
  ctx.arc(x + size + radius * 0.4, y + half, radius, Math.PI * 1.2, Math.PI * 0.8, false);
  ctx.lineTo(x + size, y + size);
  // 下边
  ctx.lineTo(x, y + size);
  // 左边
  ctx.lineTo(x, y);
  ctx.closePath();
};

// 生成随机彩色形状的背景图
const generateRandomBackground = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
) => {
  // 渐变底色
  const bgGrad = ctx.createLinearGradient(0, 0, width, height);
  const hue1 = randomInt(0, 360);
  const hue2 = (hue1 + randomInt(60, 180)) % 360;
  bgGrad.addColorStop(0, `hsl(${hue1}, 65%, 75%)`);
  bgGrad.addColorStop(1, `hsl(${hue2}, 65%, 65%)`);
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, width, height);

  // 随机形状
  const shapeCount = randomInt(8, 14);
  for (let i = 0; i < shapeCount; i++) {
    const shapeType = randomInt(0, 2);
    const cx = randomInt(0, width);
    const cy = randomInt(0, height);
    const size = randomInt(20, 70);
    const hue = randomInt(0, 360);
    const alpha = (randomInt(30, 70) / 100).toFixed(2);
    ctx.fillStyle = `hsla(${hue}, 70%, 60%, ${alpha})`;
    ctx.beginPath();
    if (shapeType === 0) {
      // 圆
      ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
    } else if (shapeType === 1) {
      // 矩形
      ctx.rect(cx - size / 2, cy - size / 2, size, size * 0.7);
    } else {
      // 三角形
      ctx.moveTo(cx, cy - size / 2);
      ctx.lineTo(cx + size / 2, cy + size / 2);
      ctx.lineTo(cx - size / 2, cy + size / 2);
    }
    ctx.closePath();
    ctx.fill();
  }

  // 添加一些小装饰点
  for (let i = 0; i < 20; i++) {
    const dx = randomInt(0, width);
    const dy = randomInt(0, height);
    const dr = randomInt(2, 6);
    ctx.fillStyle = `rgba(255,255,255,${(randomInt(20, 50) / 100).toFixed(2)})`;
    ctx.beginPath();
    ctx.arc(dx, dy, dr, 0, Math.PI * 2);
    ctx.fill();
  }
};

const SliderCaptcha: React.FC<SliderCaptchaProps> = ({ visible, onSuccess, onClose }) => {
  const bgCanvasRef = useRef<HTMLCanvasElement>(null);
  const pieceCanvasRef = useRef<HTMLCanvasElement>(null);
  const [sliderX, setSliderX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'fail'>('idle');
  const [targetX, setTargetX] = useState(0);
  const dragStartRef = useRef(0);
  const sliderStartRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // 初始化/刷新拼图
  const initCaptcha = useCallback(() => {
    const bgCanvas = bgCanvasRef.current;
    const pieceCanvas = pieceCanvasRef.current;
    if (!bgCanvas || !pieceCanvas) return;

    const bgCtx = bgCanvas.getContext('2d');
    const pieceCtx = pieceCanvas.getContext('2d');
    if (!bgCtx || !pieceCtx) return;

    // 随机拼图位置（确保拼图块不在太左边，给拖动留空间）
    const px = randomInt(CANVAS_WIDTH * 0.4, CANVAS_WIDTH - PIECE_SIZE - 20);
    const py = randomInt(PIECE_RADIUS + 10, CANVAS_HEIGHT - PIECE_SIZE - 10);
    setTargetX(px);
    setSliderX(0);
    setStatus('idle');

    // 1. 绘制随机背景
    bgCtx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    generateRandomBackground(bgCtx, CANVAS_WIDTH, CANVAS_HEIGHT);

    // 2. 从背景中提取拼图块图像数据
    // 先保存完整背景
    const bgImageData = bgCtx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // 3. 在背景上绘制缺口（半透明遮罩）
    bgCtx.save();
    drawPiecePath(bgCtx, px, py, PIECE_SIZE, PIECE_RADIUS);
    bgCtx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    bgCtx.fill();
    bgCtx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
    bgCtx.lineWidth = 1.5;
    bgCtx.stroke();
    bgCtx.restore();

    // 4. 绘制拼图块到 pieceCanvas
    pieceCanvas.width = PIECE_SIZE + PIECE_RADIUS + 4;
    pieceCanvas.height = CANVAS_HEIGHT;
    pieceCtx.clearRect(0, 0, pieceCanvas.width, pieceCanvas.height);

    // 先把完整背景放到临时 canvas
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = CANVAS_WIDTH;
    tempCanvas.height = CANVAS_HEIGHT;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;
    tempCtx.putImageData(bgImageData, 0, 0);

    // 用 clip 裁剪出拼图块
    pieceCtx.save();
    drawPiecePath(pieceCtx, 0, py, PIECE_SIZE, PIECE_RADIUS);
    pieceCtx.clip();
    pieceCtx.drawImage(tempCanvas, -px, 0);
    pieceCtx.restore();

    // 拼图块边框
    pieceCtx.save();
    drawPiecePath(pieceCtx, 0, py, PIECE_SIZE, PIECE_RADIUS);
    pieceCtx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    pieceCtx.lineWidth = 2;
    pieceCtx.stroke();
    // 添加阴影效果
    pieceCtx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    pieceCtx.shadowBlur = 6;
    pieceCtx.shadowOffsetX = 2;
    pieceCtx.shadowOffsetY = 2;
    pieceCtx.stroke();
    pieceCtx.restore();
  }, []);

  useEffect(() => {
    if (visible) {
      // 延迟一帧确保 canvas 已挂载
      requestAnimationFrame(() => {
        initCaptcha();
      });
    }
  }, [visible, initCaptcha]);

  // 拖动事件处理
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (status === 'success') return;
    setIsDragging(true);
    setStatus('idle');
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    dragStartRef.current = clientX;
    sliderStartRef.current = sliderX;
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const maxSlide = CANVAS_WIDTH - 50;
      let newX = sliderStartRef.current + (clientX - dragStartRef.current);
      newX = Math.max(0, Math.min(newX, maxSlide));
      setSliderX(newX);
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, sliderX]);

  // 拖动结束后验证
  useEffect(() => {
    if (isDragging || sliderX === 0) return;

    if (Math.abs(sliderX - targetX) <= TOLERANCE) {
      setStatus('success');
      setTimeout(() => {
        onSuccess();
      }, 500);
    } else {
      setStatus('fail');
      // 失败后短暂提示，然后重置
      setTimeout(() => {
        setSliderX(0);
        setStatus('idle');
        initCaptcha();
      }, 800);
    }
  }, [isDragging, sliderX, targetX, onSuccess, initCaptcha]);

  if (!visible) return null;

  const sliderTrackWidth = CANVAS_WIDTH - 16;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={containerRef}
        style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
          width: CANVAS_WIDTH + 40,
          userSelect: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
            安全验证
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <span
              style={{ cursor: 'pointer', fontSize: '18px', color: '#999', lineHeight: 1 }}
              title="刷新"
              onClick={() => initCaptcha()}
            >
              ↻
            </span>
            <span
              style={{ cursor: 'pointer', fontSize: '18px', color: '#999', lineHeight: 1 }}
              title="关闭"
              onClick={onClose}
            >
              ✕
            </span>
          </div>
        </div>

        {/* 拼图画布区域 */}
        <div style={{ position: 'relative', width: CANVAS_WIDTH, height: CANVAS_HEIGHT, borderRadius: '8px', overflow: 'hidden' }}>
          <canvas
            ref={bgCanvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            style={{ display: 'block', borderRadius: '8px' }}
          />
          <canvas
            ref={pieceCanvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: sliderX,
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* 滑块轨道 */}
        <div
          style={{
            marginTop: '14px',
            position: 'relative',
            height: '44px',
            background: status === 'success' ? '#d4edda' : status === 'fail' ? '#f8d7da' : '#f0f2f5',
            borderRadius: '22px',
            border: `1px solid ${status === 'success' ? '#28a745' : status === 'fail' ? '#dc3545' : '#e0e0e0'}`,
            transition: 'background 0.3s, border-color 0.3s',
          }}
        >
          {/* 提示文字 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '13px',
              color: status === 'success' ? '#28a745' : status === 'fail' ? '#dc3545' : '#aaa',
              pointerEvents: 'none',
              fontWeight: 500,
              transition: 'color 0.3s',
            }}
          >
            {status === 'success' ? '✓ 验证成功' : status === 'fail' ? '✗ 验证失败，请重试' : '拖动滑块完成拼图'}
          </div>

          {/* 已滑过的轨道填充 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: sliderX + 22,
              height: '100%',
              borderRadius: '22px',
              background: status === 'success'
                ? 'linear-gradient(135deg, #28a745, #20c997)'
                : status === 'fail'
                  ? 'linear-gradient(135deg, #dc3545, #e74c3c)'
                  : 'linear-gradient(135deg, #667eea, #764ba2)',
              opacity: sliderX > 0 ? 0.2 : 0,
              transition: status !== 'idle' ? 'opacity 0.3s' : 'none',
            }}
          />

          {/* 滑块按钮 */}
          <div
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            style={{
              position: 'absolute',
              top: '2px',
              left: sliderX,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: status === 'success'
                ? 'linear-gradient(135deg, #28a745, #20c997)'
                : status === 'fail'
                  ? 'linear-gradient(135deg, #dc3545, #e74c3c)'
                  : 'linear-gradient(135deg, #667eea, #764ba2)',
              boxShadow: isDragging
                ? '0 4px 16px rgba(102, 126, 234, 0.5)'
                : '0 2px 8px rgba(0,0,0,0.2)',
              cursor: status === 'success' ? 'default' : 'grab',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: status !== 'idle' ? 'background 0.3s' : 'none',
              transform: isDragging ? 'scale(1.1)' : 'scale(1)',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              {status === 'success' ? (
                <polyline points="20 6 9 17 4 12" />
              ) : status === 'fail' ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </>
              )}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SliderCaptcha;
